// frontend/src/pages/RequestDetailPage.jsx

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import {
  MapPin, Calendar, Home, DollarSign, Clock,
  CheckCircle, XCircle, Loader2, ArrowRight,
  Briefcase, Star, Send, AlertCircle, Eye,
  User, Phone, MessageCircle, FileText, Users,
  Zap, CreditCard, ChevronLeft, TrendingUp
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { PageSEO } from '../components/SEO/PageSEO';
import config from '../config';

export const RequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [request, setRequest] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [showBidForm, setShowBidForm] = useState(false);
  const [submittingBid, setSubmittingBid] = useState(false);
  const [bidData, setBidData] = useState({
    proposed_cost: '',
    proposed_duration: 3,
    description: ''
  });

  const isInstaller = user?.role === 'installer';
  const isCustomer = user?.role === 'customer';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // دریافت اطلاعات درخواست
      const reqRes = await fetch(config.getApiUrl(`/installations/requests/${id}/`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (reqRes.ok) {
        const reqData = await reqRes.json();
        console.log('Request data:', reqData);
        setRequest(reqData);
      } else if (reqRes.status === 404) {
        toast.error('درخواست یافت نشد');
        navigate('/installations');
        return;
      }

      // دریافت پیشنهادات
      const bidsRes = await fetch(config.getApiUrl(`/marketplace/bids/?installation_request=${id}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (bidsRes.ok) {
        const bidsData = await bidsRes.json();
        const bidsList = bidsData.results || bidsData;
        setBids(bidsList);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('خطا در دریافت اطلاعات');
    } finally {
      setLoading(false);
    }
  };

  // ========== توابع کمکی ==========

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { text: 'در انتظار پیشنهاد', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      bidding: { text: 'در حال دریافت پیشنهاد', color: 'bg-blue-100 text-blue-800', icon: Users },
      quoted: { text: 'پیشنهاد داده شده', color: 'bg-purple-100 text-purple-800', icon: DollarSign },
      accepted: { text: 'تایید شده', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      scheduled: { text: 'زمان‌بندی شده', color: 'bg-indigo-100 text-indigo-800', icon: Calendar },
      in_progress: { text: 'در حال انجام', color: 'bg-orange-100 text-orange-800', icon: Zap },
      completed: { text: 'تکمیل شده', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      cancelled: { text: 'لغو شده', color: 'bg-red-100 text-red-800', icon: XCircle },
    };
    const info = statusMap[status] || statusMap.pending;
    const Icon = info.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-full ${info.color}`}>
        <Icon className="w-4 h-4" />
        {info.text}
      </span>
    );
  };

  const getBidStatusBadge = (status) => {
    const statusMap = {
      'pending': { text: 'در انتظار بررسی', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'accepted': { text: 'پذیرفته شده 🎉', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'rejected': { text: 'رد شده', color: 'bg-red-100 text-red-800', icon: XCircle },
      'expired': { text: 'منقضی شده', color: 'bg-gray-100 text-gray-600', icon: XCircle },
    };
    const info = statusMap[status] || statusMap.pending;
    const Icon = info.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${info.color}`}>
        <Icon className="w-3 h-3" />
        {info.text}
      </span>
    );
  };

  const formatPrice = (price) => {
    if (!price) return '۰';
    return parseInt(price).toLocaleString('fa-IR');
  };

  const getCustomerName = (request) => {
    if (request?.customer?.full_name) return request.customer.full_name;
    if (request?.customer?.username) return request.customer.username;
    if (request?.customer?.phone) return request.customer.phone;
    return 'مشتری';
  };

  const getCustomerPhone = (request) => {
    if (request?.customer?.phone) return request.customer.phone;
    return '-';
  };

  // ========== عملیات‌ها ==========

  const handleAcceptBid = async (bidId, installerId) => {
    if (!window.confirm('آیا از پذیرش این پیشنهاد اطمینان دارید؟')) return;

    setAccepting(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/marketplace/bids/${bidId}/accept/`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('✅ پیشنهاد با موفقیت پذیرفته شد');
        
        setRequest(prev => ({
          ...prev,
          status: 'accepted',
          contract_id: data.contract_id
        }));
        
        navigate(`/contract/${data.contract_id}`);
      } else {
        const error = await response.json();
        toast.error(error.error || 'خطا در پذیرش پیشنهاد');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('خطا در ارتباط با سرور');
    } finally {
      setAccepting(false);
    }
  };

  const handleSubmitBid = async (e) => {
    e.preventDefault();

    if (!bidData.proposed_cost) {
      toast.error('لطفاً مبلغ پیشنهادی را وارد کنید');
      return;
    }

    if (parseInt(bidData.proposed_cost) <= 0) {
      toast.error('مبلغ پیشنهادی باید بزرگتر از صفر باشد');
      return;
    }

    setSubmittingBid(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/installations/requests/${id}/submit_bid/`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          proposed_cost: parseInt(bidData.proposed_cost),
          proposed_duration: parseInt(bidData.proposed_duration),
          description: bidData.description
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('✅ پیشنهاد با موفقیت ارسال شد');
        setShowBidForm(false);
        setBidData({ proposed_cost: '', proposed_duration: 3, description: '' });
        fetchData();
      } else {
        toast.error(data.error || 'خطا در ارسال پیشنهاد');
      }
    } catch (error) {
      console.error('Error submitting bid:', error);
      toast.error('خطا در ارتباط با سرور');
    } finally {
      setSubmittingBid(false);
    }
  };

  // ========== بررسی‌ها ==========

  const hasSubmittedBid = isInstaller && bids.some(bid => {
    return bid.installer?.user?.id === user?.id || bid.installer?.id === user?.installer_profile?.id;
  });

  const getUserBid = () => {
    return bids.find(bid => 
      bid.installer?.user?.id === user?.id || 
      bid.installer?.id === user?.installer_profile?.id
    );
  };

  const canSubmitBid = isInstaller &&
    (request?.status === 'pending' || request?.status === 'bidding') &&
    !hasSubmittedBid;

  const isBiddingClosed = request?.status !== 'pending' && request?.status !== 'bidding';

  // ========== رندر ==========

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900">درخواست یافت نشد</h2>
          <Link to="/installations" className="mt-4 inline-block text-blue-600">بازگشت</Link>
        </div>
      </div>
    );
  }

  const userBid = getUserBid();

  return (
    <>
      <PageSEO title={`درخواست نصب #${request.id} | سان ایران`} noindex={true} />
      
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* دکمه بازگشت */}
          <div className="mb-6">
            <Link 
              to={isInstaller ? '/installations' : '/installations'} 
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 transition"
            >
              <ChevronLeft className="w-4 h-4" />
              بازگشت به لیست درخواست‌ها
            </Link>
          </div>

          {/* هدر درخواست */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="flex flex-wrap justify-between items-center gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-900">
                    درخواست نصب #{request.id?.toString().slice(-6)}
                  </h1>
                  {getStatusBadge(request.status)}
                </div>
                <p className="text-gray-500 mt-1">
                  ثبت شده در {new Date(request.created_at).toLocaleDateString('fa-IR')}
                </p>
              </div>
              
              {/* دکمه ارسال پیشنهاد - برای نصاب */}
              {canSubmitBid && (
                <button
                  onClick={() => setShowBidForm(!showBidForm)}
                  className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition shadow-lg shadow-green-200"
                >
                  <Send className="w-4 h-4" />
                  {showBidForm ? 'بستن فرم' : 'ارسال پیشنهاد قیمت'}
                </button>
              )}
            </div>
          </div>

          {/* اطلاعات درخواست */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {/* مشتری */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">مشتری</p>
                  <p className="font-medium text-gray-900">{getCustomerName(request)}</p>
                </div>
              </div>
              {getCustomerPhone(request) !== '-' && (
                <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                  <Phone className="w-4 h-4" />
                  <span>{getCustomerPhone(request)}</span>
                </div>
              )}
            </div>

            {/* آدرس */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">آدرس</p>
                  <p className="font-medium text-gray-900">{request.address}</p>
                </div>
              </div>
              <div className="flex gap-4 text-sm text-gray-600 mt-2">
                <span>مساحت: {request.roof_area} m²</span>
                <span>نوع سقف: {request.roof_type || '-'}</span>
              </div>
            </div>

            {/* تاریخ و هزینه */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">تاریخ ترجیحی</p>
                  <p className="font-medium text-gray-900">
                    {new Date(request.preferred_date).toLocaleDateString('fa-IR')}
                  </p>
                </div>
              </div>
              {request.monthly_bill && (
                <div className="flex items-center gap-2 text-sm text-gray-600 mt-2">
                  <CreditCard className="w-4 h-4" />
                  <span>قبض برق: {formatPrice(request.monthly_bill)} تومان</span>
                </div>
              )}
            </div>
          </div>

          {/* توضیحات */}
          {request.description && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6">
              <h3 className="font-medium text-gray-700 mb-2">📝 توضیحات درخواست</h3>
              <p className="text-gray-600">{request.description}</p>
            </div>
          )}

          {/* ✅ فرم ارسال پیشنهاد - برای نصاب */}
          {isInstaller && showBidForm && canSubmitBid && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-200 p-6 mb-6">
              <h3 className="text-lg font-bold text-green-800 mb-4 flex items-center gap-2">
                <Send className="w-5 h-5" />
                ارسال پیشنهاد قیمت
              </h3>
              <form onSubmit={handleSubmitBid} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      مبلغ پیشنهادی (تومان) *
                    </label>
                    <input
                      type="number"
                      value={bidData.proposed_cost}
                      onChange={(e) => setBidData({ ...bidData, proposed_cost: e.target.value })}
                      className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="مثال: 25000000"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      مدت زمان اجرا (روز) *
                    </label>
                    <input
                      type="number"
                      value={bidData.proposed_duration}
                      onChange={(e) => setBidData({ ...bidData, proposed_duration: e.target.value })}
                      className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      min="1"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    یادداشت پیشنهاد
                  </label>
                  <textarea
                    value={bidData.description}
                    onChange={(e) => setBidData({ ...bidData, description: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                    placeholder="شرح خدمات، ضمانت‌ها، تجهیزات مورد استفاده..."
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowBidForm(false)}
                    className="px-6 py-2.5 border border-gray-300 rounded-xl hover:bg-gray-50 transition font-medium"
                  >
                    انصراف
                  </button>
                  <button
                    type="submit"
                    disabled={submittingBid}
                    className="flex-1 px-6 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submittingBid ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        در حال ارسال...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        ارسال پیشنهاد
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ✅ نمایش پیشنهاد کاربر (نصاب) */}
          {isInstaller && hasSubmittedBid && userBid && (
            <div className={`rounded-2xl p-5 mb-6 border-2 ${
              userBid.status === 'accepted' ? 'border-green-300 bg-green-50' :
              userBid.status === 'rejected' ? 'border-red-300 bg-red-50' :
              'border-blue-300 bg-blue-50'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-gray-900 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    پیشنهاد شما
                  </h3>
                  <div className="mt-2">
                    <span className="text-2xl font-bold text-gray-900">
                      {formatPrice(userBid.proposed_cost)} تومان
                    </span>
                    <span className="text-gray-500 mr-3">
                      • {userBid.proposed_duration} روز
                    </span>
                  </div>
                  {userBid.description && (
                    <p className="text-gray-600 mt-2 text-sm">
                      <span className="text-gray-400">یادداشت:</span> {userBid.description}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs text-gray-400">
                      ارسال: {new Date(userBid.created_at).toLocaleDateString('fa-IR')}
                    </span>
                    {getBidStatusBadge(userBid.status)}
                  </div>
                </div>
                {userBid.status === 'accepted' && (
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <p className="text-sm font-medium text-green-700 mt-1">پذیرفته شده 🎉</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ✅ وضعیت درخواست - برای همه */}
          {isBiddingClosed && !hasSubmittedBid && isInstaller && (
            <div className="bg-gray-50 rounded-2xl border border-gray-200 p-5 mb-6 text-center">
              <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">زمان ارسال پیشنهاد برای این درخواست به پایان رسیده است</p>
              <p className="text-sm text-gray-400 mt-1">وضعیت فعلی: {getStatusBadge(request.status)}</p>
            </div>
          )}

          {/* ✅ لینک قرارداد - برای مشتری و نصاب */}
          {(request.status === 'accepted' || request.status === 'scheduled' || request.status === 'in_progress') && request.contract_id && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-5 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">قرارداد نصب</p>
                    <p className="text-sm text-gray-500">
                      قرارداد برای درخواست #{request.id?.toString().slice(-6)} ایجاد شده است
                    </p>
                  </div>
                </div>
                <Link
                  to={`/contract/${request.contract_id}`}
                  className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition shadow-lg shadow-blue-200"
                >
                  <FileText className="w-4 h-4" />
                  مشاهده و امضای قرارداد
                </Link>
              </div>
            </div>
          )}

          {/* ✅ لیست پیشنهادات - برای مشتری */}
          {isCustomer && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  پیشنهادات نصاب‌ها ({bids.length})
                </h2>
                {request.status === 'pending' || request.status === 'bidding' ? (
                  <span className="text-sm text-green-600 bg-green-50 px-3 py-1 rounded-full">
                    در حال دریافت پیشنهاد
                  </span>
                ) : (
                  <span className="text-sm text-gray-500 bg-gray-50 px-3 py-1 rounded-full">
                    پذیرش پیشنهاد به پایان رسیده
                  </span>
                )}
              </div>

              {bids.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Clock className="w-14 h-14 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">هنوز پیشنهادی ارسال نشده است</p>
                  <p className="text-sm mt-1">لطفاً چند روز دیگر مراجعه کنید</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {bids.map((bid) => {
                    const isExpired = new Date(bid.expires_at) < new Date();
                    const isAccepted = bid.status === 'accepted';
                    const isBestBid = bids.length > 0 && bid.proposed_cost === Math.min(...bids.map(b => b.proposed_cost));
                    
                    return (
                      <div 
                        key={bid.id} 
                        className={`rounded-xl p-5 border-2 transition ${
                          isAccepted ? 'border-green-300 bg-green-50' :
                          isBestBid && bid.status === 'pending' ? 'border-yellow-300 bg-yellow-50' :
                          'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex flex-wrap justify-between items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                <Briefcase className="w-6 h-6 text-blue-600" />
                              </div>
                              <div>
                                <h3 className="font-bold text-lg text-gray-900">
                                  {bid.installer?.company_name || 'نصاب'}
                                </h3>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                                  <span>{bid.installer?.rating || 'جدید'}</span>
                                  <span>•</span>
                                  <span>{bid.installer?.experience_years || 0} سال تجربه</span>
                                </div>
                              </div>
                              {isBestBid && bid.status === 'pending' && (
                                <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded-full">
                                  بهترین قیمت
                                </span>
                              )}
                              {isAccepted && (
                                <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">
                                  پذیرفته شده
                                </span>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                              <div>
                                <span className="text-gray-500 text-sm">مبلغ پیشنهادی:</span>
                                <span className="font-bold text-blue-600 mr-2 text-lg">
                                  {formatPrice(bid.proposed_cost)} تومان
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500 text-sm">مدت زمان اجرا:</span>
                                <span className="font-medium mr-2">{bid.proposed_duration} روز</span>
                              </div>
                            </div>
                            
                            {bid.description && (
                              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                <p className="text-sm text-gray-600">
                                  <span className="text-gray-400">📝 یادداشت:</span> {bid.description}
                                </p>
                              </div>
                            )}
                            
                            <div className="flex items-center gap-3 mt-3">
                              <span className="text-xs text-gray-400">
                                ارسال: {new Date(bid.created_at).toLocaleDateString('fa-IR')}
                              </span>
                              {isExpired && bid.status === 'pending' && (
                                <span className="text-xs text-orange-500 bg-orange-50 px-2 py-0.5 rounded-full">
                                  منقضی شده
                                </span>
                              )}
                              {bid.status === 'rejected' && (
                                <span className="text-xs text-red-500 bg-red-50 px-2 py-0.5 rounded-full">
                                  رد شده
                                </span>
                              )}
                            </div>
                          </div>

                          {/* دکمه انتخاب نصاب - فقط برای مشتری در وضعیت pending/bidding */}
                          {(request.status === 'pending' || request.status === 'bidding') && 
                           bid.status === 'pending' && !isExpired && (
                            <button
                              onClick={() => handleAcceptBid(bid.id, bid.installer?.id)}
                              disabled={accepting}
                              className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition disabled:opacity-50 shadow-lg shadow-green-200"
                            >
                              {accepting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <CheckCircle className="w-4 h-4" />
                              )}
                              انتخاب این نصاب
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};